import { type RefObject, useEffect } from 'react';
import { useShallow } from 'zustand/shallow';

import { isBatchMatrixSkipEnabled } from 'app/modules/wallforming/components/scenes/objects/articles/hooks/useInstanceMatrixSyncHandler';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { onBatchComplete } from 'app/modules/wallforming/components/state-management/placement/slices/connectionSlice';
import { yieldToMain } from 'app/modules/wallforming/components/state-management/placement/utils/schedulerUtils';
import { MATRIX_UPDATE_CHUNK_SIZE } from 'shared/constants';

import type { ArticleReference } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleReference';

interface BatchUpdateHandlerProps {
    isMainCanvas: boolean;
    articleRef: RefObject<ArticleReference>;
    pendingColliderIdsRef: RefObject<Set<string> | null>;
    needsBatchMatrixUpdateRef: RefObject<boolean>;
    chunkUpdateInProgressRef: RefObject<boolean>;
    chunkUpdateIdRef: RefObject<number>;
    pendingUpdateIdsRef: RefObject<Set<string> | null>;
}

/**
 * Handles batch updates from the hub connection for article instance matrices and collider state, chunking updates for performance.
 */
export const useBatchUpdateHandler = ({
    isMainCanvas,
    articleRef,
    pendingColliderIdsRef,
    needsBatchMatrixUpdateRef,
    chunkUpdateInProgressRef,
    chunkUpdateIdRef,
    pendingUpdateIdsRef
}: BatchUpdateHandlerProps) => {
    const { updateInstanceMatrices, isDraggingActive } = usePlacementStore(
        useShallow((state) => ({
            updateInstanceMatrices: state.updateInstanceMatrices,
            isDraggingActive: state.isDraggingActive
        }))
    );

    useEffect(() => {
        const unsubscribeBatch = onBatchComplete((insertedIds: ReadonlySet<string>) => {
            if (!needsBatchMatrixUpdateRef.current) {
                return;
            }
            if (!isBatchMatrixSkipEnabled()) {
                needsBatchMatrixUpdateRef.current = false;
                pendingUpdateIdsRef.current = null;
                return;
            }

            needsBatchMatrixUpdateRef.current = false;

            if (isDraggingActive()) {
                pendingUpdateIdsRef.current = null;
                return;
            }

            const pendingIds = pendingUpdateIdsRef.current;
            pendingUpdateIdsRef.current = null;
            const idsToUpdate = pendingIds ? Array.from(pendingIds) : Object.keys(articleRef.current.articleInstances);

            // Keep only the IDs that belong to the current article
            const insertedArticleIds = Array.from(insertedIds).filter(
                (id) => id in articleRef.current.articleInstances
            );

            // Remove duplicates and merge ids for update
            const resolvedIdsToUpdate = [...new Set([...idsToUpdate, ...insertedArticleIds])];
            if (resolvedIdsToUpdate.length === 0) {
                return;
            }

            const showSelection = Object.keys(usePlacementStore.getState().articleInstanceSelections).some(
                (id) => id in articleRef.current.articleInstances
            );

            if (resolvedIdsToUpdate.length <= MATRIX_UPDATE_CHUNK_SIZE) {
                updateInstanceMatrices(showSelection, articleRef, true, resolvedIdsToUpdate);
                return;
            }

            // chunked update to avoid blocking the main thread
            const updateId = ++chunkUpdateIdRef.current;
            chunkUpdateInProgressRef.current = true;

            (async () => {
                for (let i = 0; i < resolvedIdsToUpdate.length; i += MATRIX_UPDATE_CHUNK_SIZE) {
                    // abort if a newer update has been requested
                    if (chunkUpdateIdRef.current !== updateId) {
                        break;
                    }

                    const chunk = resolvedIdsToUpdate.slice(i, i + MATRIX_UPDATE_CHUNK_SIZE);
                    const isLastChunk = i + MATRIX_UPDATE_CHUNK_SIZE >= resolvedIdsToUpdate.length;
                    updateInstanceMatrices(showSelection, articleRef, true, chunk);

                    if (!isLastChunk) {
                        await yieldToMain();
                    }
                }
                chunkUpdateInProgressRef.current = false;
            })();
        });

        return () => unsubscribeBatch();
    }, [
        articleRef,
        chunkUpdateIdRef,
        chunkUpdateInProgressRef,
        isDraggingActive,
        needsBatchMatrixUpdateRef,
        pendingUpdateIdsRef,
        updateInstanceMatrices
    ]);

    // ── Deferred collider update after batch processing completes ──────────────
    // Colliders depend on POIs, which are committed simultaneously with batch
    // instances. Deferring to requestAnimationFrame ensures POIs are available.
    useEffect(() => {
        if (!isMainCanvas) {
            return;
        }

        const unsubscribe = onBatchComplete(() => {
            const pendingIds = pendingColliderIdsRef.current;
            if (!pendingIds || pendingIds.size === 0) {
                return;
            }
            pendingColliderIdsRef.current = null;

            requestAnimationFrame(() => {
                const idsToUpdate = Array.from(pendingIds);
                const state = usePlacementStore.getState();
                const hasSelection = Object.keys(state.articleInstanceSelections).some(
                    (id) => id in articleRef.current.articleInstances
                );
                updateInstanceMatrices(hasSelection, articleRef, true, idsToUpdate);
            });
        });

        return () => unsubscribe();
    }, [articleRef, isMainCanvas, pendingColliderIdsRef, updateInstanceMatrices]);
};
